"use client";

import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import Button from "@/components/common/Button";
import ScrollReveal from "@/components/common/ScrollReveal";
import Tabs, { TAB_ORIENTATION, TabItem } from "@/components/common/Tab";
import { getApiErrorMessage } from "@/lib/apiError";
import {
  RECAPTCHA_FAILED_MESSAGE,
  getRecaptchaToken,
  isRecaptchaFailure,
} from "@/lib/recaptcha";
import CompromiseAssessmentIllustration from "./CompromiseAssessmentIllustration";

/** Incident intake form the "URGENT HELP" pill opens (URL taken from v1). */
const URGENT_HELP_FORM_URL = "https://forms.gle/c3vH17RvV7ZUbfEH6";

/** Captcha-only endpoint — verifies the token, sends no mail. */
const CAPTCHA_VERIFY_URL = "https://diana.e-cq.net/api/v1/users/captcha-verify";

interface StepDef {
  id: string;
  name: string;
  paragraphs: string[];
}

const steps: StepDef[] = [
  {
    id: "step-1",
    name: "Initial Consultation and Scoping",
    paragraphs: [
      "Meet with the client to understand their concerns and the extent of the suspected compromise.",
      "Gather information about the client's infrastructure, including network diagrams, asset inventories, and access controls.",
      "Define the scope of the assessment, including systems, networks, and data repositories to be examined.",
    ],
  },
  {
    id: "step-2",
    name: "Data Collection and Analysis",
    paragraphs: [
      "Collect and analyze relevant data, including system logs, network traffic, and endpoint artifacts.",
      "ECQ installs ThreatSonar agent to all the workstations and servers to scan and search for artifacts, threats, or hacking tools.",
      "Use advanced threat hunting techniques to search for signs of compromise, such as suspicious processes, network connections, and file activities.",
      "Analyze the collected data to identify indicators of compromise (IOCs), unusual patterns, and potential attack vectors.",
    ],
  },
  {
    id: "step-3",
    name: "Preparation and Planning",
    paragraphs: [
      "Develop a detailed plan and timeline for the assessment, including objectives, milestones, and deliverables.",
      "Assemble a team of skilled professionals with experience in incident response, digital forensics, and threat hunting.",
      "Obtain the necessary access credentials, permissions, and tools required for the assessment.",
    ],
  },
  {
    id: "step-4",
    name: "Incident Validation and Containment",
    paragraphs: [
      "Validate identified incidents by correlating them with known IOCs, threat intelligence, and historical data.",
      "If a compromise is confirmed, work with the client to contain the incident and prevent further damage.",
      "Implement short-term remediation measures, such as isolating affected systems, blocking malicious IP addresses, and resetting compromised credentials.",
    ],
  },
  {
    id: "step-5",
    name: "Root Cause Analysis and Remediation",
    paragraphs: [
      "Conduct a root cause analysis to determine how the breach occurred and identify any vulnerabilities or weaknesses in the client's security posture.",
      "Develop a comprehensive remediation plan that addresses these vulnerabilities and weaknesses, including patching, updating security configurations, and implementing additional security controls.",
      "Work with the client to implement the remediation plan and ensure that all necessary actions are taken to prevent future incidents.",
    ],
  },
  {
    id: "step-6",
    name: "Reporting and Documentation",
    paragraphs: [
      "Prepare a detailed report of the assessment findings, including the scope, methodology, identified compromises, root causes, and recommended remediation actions.",
      "Present the report to the client and discuss the findings, ensuring they understand the implications and necessary steps for recovery.",
      "Document all activities, decisions, and changes made during the assessment process for future reference and audit purposes.",
    ],
  },
  {
    id: "step-7",
    name: "Post-Assessment Support and Monitoring",
    paragraphs: [
      "Provide ongoing support to the client as they work to implement the recommended remediation actions, addressing any questions or concerns that may arise.",
      "Monitor the client's environment for a defined period to ensure that the implemented measures are effective in preventing further incidents.",
      "Conduct periodic follow-up assessments to verify the ongoing security and integrity of the client's systems and networks.",
    ],
  },
];

const CompromiseAssessment: React.FC = () => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [helpError, setHelpError] = useState<string | null>(null);

  // The step list is driven from here so the diagram and the panel stay in sync;
  // `Tabs` still owns everything else about the panel.
  const [activeStep, setActiveStep] = useState(steps[0].id);
  const stepsRef = useRef<HTMLDivElement>(null);
  const [stepsIn, setStepsIn] = useState(false);

  // Rows cascade in the first time the list comes into view.
  useEffect(() => {
    const el = stepsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setStepsIn(true);
        observer.disconnect();
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /**
   * v1 "URGENT HELP" flow (assets/js/compromise-assessment.js): open a tab,
   * clear a reCAPTCHA check, then point that tab at the intake form. The tab is
   * opened for the user gesture and only navigated afterwards so the popup
   * blocker lets it through; v1 closed it silently on failure, we also say why.
   */
  const handleUrgentHelp = async () => {
    if (isVerifying) return;

    // Must happen before the first `await` — after one, the user gesture is
    // gone and every browser blocks the pop-up.
    const helpWindow = window.open();
    if (!helpWindow) {
      setHelpError(
        "Your browser blocked the form window. Please allow pop-ups for this site and try again."
      );
      return;
    }

    setIsVerifying(true);
    setHelpError(null);

    try {
      const token = await getRecaptchaToken("click");

      const response = await fetch(CAPTCHA_VERIFY_URL, {
        method: "POST",
        cache: "no-cache",
        headers: { "Content-Type": "application/json" },
        redirect: "follow",
        referrerPolicy: "no-referrer",
        body: JSON.stringify({ recaptchaResponse: token }),
      });
      const result = await response.json();

      if (result?.status === "success") {
        helpWindow.location.href = URGENT_HELP_FORM_URL;
      } else {
        helpWindow.close();
        setHelpError(
          // Hide the backend's raw "Oops! Invalid reCAPTCHA" wording.
          isRecaptchaFailure(result)
            ? RECAPTCHA_FAILED_MESSAGE
            : getApiErrorMessage(result)
        );
      }
    } catch (err) {
      console.error("Urgent help verification error:", err);
      helpWindow.close();
      setHelpError(
        // A missing script / empty token throws out of getRecaptchaToken().
        isRecaptchaFailure(err)
          ? RECAPTCHA_FAILED_MESSAGE
          : "Something went wrong! Please try again."
      );
    } finally {
      setIsVerifying(false);
    }
  };

  const tabItems: TabItem[] = steps.map((step, idx) => ({
    id: step.id,
    name: step.name,
    value: step.id,
    number: idx + 1,
    content: (
      <>
        {step.paragraphs.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </>
    ),
  }));

  return (
    <section className="relative w-full px-4 sm:px-6 lg:px-8 pb-20 pt-2">
      <div className="relative z-10 mx-auto w-full max-w-[1300px]">
        {/* Top block: intro paragraphs + illustration */}
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
          {/* Left: copy */}
          <div className="flex flex-col gap-6 sm:gap-7 max-w-[540px]">
            <ScrollReveal direction="up" delay={0}>
              <p className="body-1 text-text-highlight">
                ECQ provides compromise assessment service through evaluation of
                a client&apos;s environment to determine if they have been
                compromised and to identify the extent of the breach. The
                following explains the steps involved in a compromise assessment
                service.
              </p>
            </ScrollReveal>

            <ScrollReveal direction="up" delay={100}>
              <p className="body-1 text-text-highlight">
                ECQ delivers a compromise assessment service that not only
                identifies and addresses existing compromises but also helps the
                client improve their overall security posture and reduce the
                risk of future breaches.
              </p>
            </ScrollReveal>

            {/* v1 CTA: sits under the intro copy, left-aligned, 290px wide.
                The phone size (200x48 with the compact type scale) comes from
                the shared `.ecq-cta` media query in globals.css. */}
            <ScrollReveal direction="up" delay={200} className="mt-1 lg:mt-6">
              <div className="ca-cta flex flex-col items-start gap-3">
                <Button
                  variant="fill"
                  color="primary"
                  width={290}
                  height={64}
                  customClassName="max-w-full"
                  onClick={handleUrgentHelp}
                  disabled={isVerifying}
                >
                  {isVerifying ? "VERIFYING..." : "URGENT HELP"}
                </Button>

                {helpError && (
                  <p
                    role="alert"
                    className="text-primary-600 font-normal text-[14px] leading-5 tracking-[0.25px]"
                  >
                    {helpError}
                  </p>
                )}
              </div>
            </ScrollReveal>
          </div>

          {/* Right: illustration */}
          <ScrollReveal direction="up" delay={150}>
            {/* Capped well below the column width on phones — at full bleed the
                artwork fills the viewBox edge to edge and reads oversized. */}
            <div className="relative w-full aspect-[587/496] max-w-[288px] sm:max-w-[440px] lg:max-w-[587px] mx-auto">
              <CompromiseAssessmentIllustration className="w-full h-full" />
            </div>
          </ScrollReveal>
        </div>

        {/* Step list + content panel (shared vertical Tabs) */}
        <ScrollReveal direction="up" delay={0} className="mt-16 lg:mt-24">
          <div ref={stepsRef} className={clsx("ca-steps", stepsIn && "is-in")}>
            <Tabs
              tabs={tabItems}
              orientation={TAB_ORIENTATION.VERTICAL}
              showContent
              currentValue={activeStep}
              onChange={setActiveStep}
            />
          </div>
        </ScrollReveal>
      </div>

      {/* Motion for the page chrome around the illustration (which carries its
          own SVG animation):
            • the URGENT HELP pill breathes a red glow until it is hovered or
              busy — it is the incident-response entry point on this page;
            • the step rows cascade in the first time the list is scrolled to.
          Styles reach into `Tabs` internals, hence :global() — a styled-jsx
          rule only tags elements written in this file's own JSX. */}
      <style jsx>{`
        .ca-cta :global(.ecq-cta__text) {
          animation: caUrgentPulse 2.6s ease-in-out infinite;
        }
        .ca-cta :global(.ecq-cta:hover .ecq-cta__text),
        .ca-cta :global(.ecq-cta:disabled .ecq-cta__text) {
          animation: none;
        }
        /* globals.css boxes the pill at 200x48 on phones; only the glow is this
           page's own, and an 18px halo on a 200px pill is most of what makes it
           look heavy on a small screen. */
        @media (max-width: 639px) {
          .ca-cta :global(.ecq-cta__text) {
            animation-name: caUrgentPulseSm;
          }
        }
        @keyframes caUrgentPulse {
          0%,
          100% {
            box-shadow: 0 0 0 0 rgba(236, 25, 56, 0);
          }
          50% {
            box-shadow: 0 0 18px 1px rgba(236, 25, 56, 0.55);
          }
        }
        @keyframes caUrgentPulseSm {
          0%,
          100% {
            box-shadow: 0 0 0 0 rgba(236, 25, 56, 0);
          }
          50% {
            box-shadow: 0 0 10px 0 rgba(236, 25, 56, 0.5);
          }
        }

        .ca-steps :global([data-steplist="desktop"] > *) {
          opacity: 0;
          translate: -14px 0;
        }
        .ca-steps.is-in :global([data-steplist="desktop"] > *) {
          animation: caStepIn 0.5s ease-out both;
        }
        @keyframes caStepIn {
          from {
            opacity: 0;
            translate: -14px 0;
          }
          to {
            opacity: 1;
            translate: 0 0;
          }
        }
        .ca-steps.is-in :global([data-steplist="desktop"] > *:nth-child(1)) {
          animation-delay: 0s;
        }
        .ca-steps.is-in :global([data-steplist="desktop"] > *:nth-child(2)) {
          animation-delay: 0.07s;
        }
        .ca-steps.is-in :global([data-steplist="desktop"] > *:nth-child(3)) {
          animation-delay: 0.14s;
        }
        .ca-steps.is-in :global([data-steplist="desktop"] > *:nth-child(4)) {
          animation-delay: 0.21s;
        }
        .ca-steps.is-in :global([data-steplist="desktop"] > *:nth-child(5)) {
          animation-delay: 0.28s;
        }
        .ca-steps.is-in :global([data-steplist="desktop"] > *:nth-child(6)) {
          animation-delay: 0.35s;
        }
        .ca-steps.is-in :global([data-steplist="desktop"] > *:nth-child(7)) {
          animation-delay: 0.42s;
        }

        @media (prefers-reduced-motion: reduce) {
          .ca-cta :global(.ecq-cta__text) {
            animation: none;
          }
          .ca-steps :global([data-steplist="desktop"] > *) {
            opacity: 1;
            translate: none;
          }
          .ca-steps.is-in :global([data-steplist="desktop"] > *) {
            animation: none;
          }
        }
      `}</style>
    </section>
  );
};

export default CompromiseAssessment;
